import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { LayoutDashboard, History, MessageSquare, Vote, User as UserIcon, Heart, ArrowUpRight, Send, CheckCircle2, Star, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { FourStars } from "../components/FourStars";
import { toast } from "sonner";

const TABS = [
  { id: "overview", label: "Aperçu", icon: LayoutDashboard },
  { id: "cotisations", label: "Cotisations", icon: History },
  { id: "messagerie", label: "Messagerie", icon: MessageSquare },
  { id: "sondages", label: "Sondages", icon: Vote },
  { id: "profil", label: "Profil", icon: UserIcon },
];

export default function DashboardPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={36} className="text-[#0052CC] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="dashboard-page">
      <section className="bg-gradient-to-br from-[#0A0F1A] via-[#0052CC] to-[#0A0F1A] text-white py-14 relative overflow-hidden">
        <div className="absolute inset-0 stars-pattern opacity-10" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <FourStars size={12} />
          <h1 className="font-display text-3xl md:text-5xl mt-3" data-testid="dashboard-welcome">
            Bonjour {user.name?.split(" ")[0]} <span className="text-[#D4AF37]">★</span>
          </h1>
          <p className="text-white/75 mt-2">Bienvenue dans votre espace membre A.F.B.</p>
          {user.role === "admin" && (
            <a href="/admin" className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-sm bg-[#D4AF37] hover:bg-[#B5952F] text-[#0A0F1A] text-sm font-bold uppercase tracking-wider" data-testid="dashboard-admin-link">
              Panneau d'administration →
            </a>
          )}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-10 -mt-10 relative z-10">
        <div className="bg-white shadow-xl rounded-2xl border border-black/5 overflow-hidden">
          <div className="flex flex-wrap border-b border-black/5" data-testid="dashboard-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.id ? "border-[#D4AF37] text-[#0A0F1A]" : "border-transparent text-[#475569] hover:text-[#0052CC]"
                }`}
              >
                <t.icon size={16} /> {t.label}
              </button>
            ))}
          </div>

          <div className="p-6 md:p-10">
            {tab === "overview" && <Overview user={user} setTab={setTab} />}
            {tab === "cotisations" && <Cotisations />}
            {tab === "messagerie" && <Messagerie user={user} />}
            {tab === "sondages" && <Sondages />}
            {tab === "profil" && <Profil user={user} />}
          </div>
        </div>
      </div>

      <div className="h-20" />
    </div>
  );
}

function Overview({ user, setTab }) {
  const [stats, setStats] = useState({ total: 0, count: 0 });
  const [polls, setPolls] = useState([]);

  useEffect(() => {
    api.get("/cotisations").then((r) => setStats({ total: r.data.total, count: r.data.count })).catch(() => {});
    api.get("/polls").then((r) => setPolls(r.data.slice(0, 2))).catch(() => {});
  }, []);

  return (
    <div data-testid="overview-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="rounded-xl bg-gradient-to-br from-[#0052CC] to-[#003D99] text-white p-6">
          <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">Cotisations cumulées</div>
          <div className="font-display text-4xl mt-2">${stats.total}</div>
          <div className="text-sm text-white/70 mt-1">{stats.count} contributions</div>
        </div>
        <div className="rounded-xl border border-black/5 bg-white p-6 shadow-sm">
          <div className="text-xs uppercase tracking-[0.2em] text-[#475569]">Statut</div>
          <div className="font-display text-2xl mt-2 text-[#0A0F1A]">Membre {user.role === "admin" ? "Admin" : "Actif"}</div>
          <div className="flex items-center gap-1.5 text-xs text-[#0052CC] mt-2"><CheckCircle2 size={14} /> Compte vérifié</div>
        </div>
        <div className="rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-6">
          <div className="text-xs uppercase tracking-[0.2em] text-[#0A0F1A]">Sondages en cours</div>
          <div className="font-display text-4xl mt-2 text-[#0A0F1A]">{polls.length}</div>
          <button onClick={() => setTab("sondages")} className="text-sm text-[#0052CC] font-semibold mt-2 inline-flex items-center gap-1" data-testid="overview-go-polls">
            Participer <ArrowUpRight size={14} />
          </button>
        </div>
      </div>

      <div className="mt-8 rounded-xl border border-black/5 bg-white p-6">
        <h3 className="font-display text-xl text-[#0A0F1A]">Mobilisez-vous</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          <Button asChild className="bg-[#D4AF37] hover:bg-[#B5952F] text-[#0A0F1A] rounded-sm" data-testid="overview-donate-btn">
            <a href="/dons"><Heart size={16} className="mr-2" /> Faire un don</a>
          </Button>
          <Button onClick={() => setTab("messagerie")} variant="outline" className="rounded-sm">
            <MessageSquare size={16} className="mr-2" /> Messagerie
          </Button>
          <Button onClick={() => setTab("sondages")} variant="outline" className="rounded-sm">
            <Vote size={16} className="mr-2" /> Sondages & Élections
          </Button>
        </div>
      </div>
    </div>
  );
}

function Cotisations() {
  const [data, setData] = useState({ total: 0, count: 0, items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/cotisations").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="cotisations-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h2 className="font-display text-2xl text-[#0A0F1A]">Historique des cotisations</h2>
          <p className="text-sm text-[#475569]">Total cumulé : <strong className="text-[#0A0F1A]">${data.total}</strong> · {data.count} contribution(s)</p>
        </div>
        <Button asChild className="bg-[#0052CC] hover:bg-[#003D99] text-white rounded-sm">
          <a href="/dons"><Heart size={16} className="mr-2" /> Nouveau don</a>
        </Button>
      </div>
      {loading ? (
        <div className="py-10 text-center"><Loader2 className="animate-spin text-[#0052CC] mx-auto" /></div>
      ) : data.items.length === 0 ? (
        <div className="py-12 text-center bg-[#FAFAFA] rounded-lg border border-dashed border-black/10">
          <Heart size={36} className="text-[#D4AF37] mx-auto" />
          <p className="text-sm text-[#475569] mt-3">Aucune cotisation enregistrée. Soutenez la cause !</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-black/5">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[#475569] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3.5">Date</th>
                <th className="text-left px-5 py-3.5">Désignation</th>
                <th className="text-left px-5 py-3.5">Méthode</th>
                <th className="text-right px-5 py-3.5">Montant</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((c, i) => (
                <tr key={c.id} data-testid={`cot-row-${i}`} className="border-t border-black/5">
                  <td className="px-5 py-4 text-[#475569]">{new Date(c.created_at).toLocaleDateString("fr-FR")}</td>
                  <td className="px-5 py-4 text-[#0A0F1A]">{c.label}</td>
                  <td className="px-5 py-4"><span className="text-xs uppercase tracking-wider text-[#0052CC]">{c.method?.replace("_", " ")}</span></td>
                  <td className="px-5 py-4 text-right font-display text-lg text-[#0A0F1A]">${c.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Messagerie({ user }) {
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [target, setTarget] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const refresh = async () => {
    const [u, m] = await Promise.all([api.get("/messages/users"), api.get("/messages")]);
    setUsers(u.data);
    setMessages(m.data);
    if (!target && u.data[0]) setTarget(u.data[0].user_id);
  };

  useEffect(() => { refresh(); }, []);

  const conv = messages.filter(
    (m) => (m.sender_id === target && m.recipient_id === user.user_id) ||
           (m.recipient_id === target && m.sender_id === user.user_id)
  );

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !target) return;
    setSending(true);
    try {
      await api.post("/messages", { recipient_id: target, content: text });
      setText("");
      await refresh();
    } catch {
      toast.error("Échec de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[560px]" data-testid="messagerie-tab">
      <div className="md:col-span-1 border border-black/5 rounded-xl bg-white overflow-y-auto">
        <div className="px-4 py-3 border-b border-black/5 text-xs uppercase tracking-[0.2em] text-[#475569]">Membres</div>
        {users.length === 0 ? (
          <div className="p-6 text-sm text-[#475569]">Aucun autre membre pour l'instant.</div>
        ) : users.map((u) => (
          <button key={u.user_id} onClick={() => setTarget(u.user_id)} data-testid={`msg-user-${u.user_id}`}
            className={`w-full text-left px-4 py-3 border-b border-black/5 hover:bg-[#FAFAFA] flex items-center gap-3 ${target === u.user_id ? "bg-[#0052CC]/5" : ""}`}>
            <div className="w-9 h-9 rounded-full bg-[#0052CC] text-white flex items-center justify-center text-sm font-semibold">
              {u.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[#0A0F1A] truncate">{u.name}</div>
              <div className="text-xs text-[#475569] truncate">{u.email}</div>
            </div>
          </button>
        ))}
      </div>

      <div className="md:col-span-2 border border-black/5 rounded-xl bg-white flex flex-col">
        <div className="px-5 py-3 border-b border-black/5 flex items-center gap-2">
          <FourStars size={11} />
          <span className="text-sm text-[#475569]">Conversation chiffrée — A.F.B.</span>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-2 bg-[#FAFAFA]" data-testid="msg-conversation">
          {conv.length === 0 ? (
            <div className="text-center text-sm text-[#475569] py-10">Aucun message — démarrez la conversation.</div>
          ) : conv.map((m, i) => (
            <div key={i} className={`flex ${m.sender_id === user.user_id ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] px-3.5 py-2 rounded-2xl text-sm ${
                m.sender_id === user.user_id ? "bg-[#0052CC] text-white rounded-br-sm" : "bg-white border border-black/5 text-[#0A0F1A] rounded-bl-sm"
              }`}>
                {m.content}
                <div className={`text-[10px] mt-1 ${m.sender_id === user.user_id ? "text-white/65" : "text-[#475569]"}`}>
                  {new Date(m.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={send} className="p-3 border-t border-black/5 flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Votre message..." disabled={!target || sending}
            data-testid="msg-input"
            className="flex-1 px-4 py-2.5 rounded-md border border-black/10 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm" />
          <Button type="submit" disabled={sending || !text.trim() || !target} data-testid="msg-send-btn"
            className="bg-[#0052CC] hover:bg-[#003D99] text-white rounded-sm">
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Sondages() {
  const [polls, setPolls] = useState([]);
  const [voting, setVoting] = useState(null);

  const load = async () => {
    const { data } = await api.get("/polls");
    setPolls(data);
  };

  useEffect(() => { load(); }, []);

  const submit = async (poll_id, option_id) => {
    setVoting(poll_id);
    try {
      await api.post(`/polls/${poll_id}/vote`, { option_id });
      toast.success("Vote enregistré !");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du vote");
    } finally {
      setVoting(null);
    }
  };

  return (
    <div data-testid="sondages-tab" className="space-y-6">
      {polls.map((p, idx) => (
        <div key={p.id} className="border border-black/5 rounded-xl bg-white p-6" data-testid={`poll-card-${idx}`}>
          <div className="flex items-start gap-3">
            <Star size={18} fill="#D4AF37" stroke="#D4AF37" className="mt-1" />
            <div className="flex-1">
              <h3 className="font-display text-xl text-[#0A0F1A]">{p.question}</h3>
              <p className="text-sm text-[#475569] mt-1">{p.description}</p>
            </div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#475569]">{p.total_votes} votes</div>
          </div>
          <div className="space-y-2.5 mt-5">
            {p.options.map((o) => {
              const pct = p.total_votes ? Math.round((o.votes / p.total_votes) * 100) : 0;
              const voted = p.user_voted === o.option_id;
              return (
                <button
                  key={o.option_id}
                  onClick={() => submit(p.id, o.option_id)}
                  disabled={voting === p.id}
                  data-testid={`poll-${idx}-option-${o.option_id}`}
                  className={`w-full text-left relative overflow-hidden rounded-md border-2 transition-all ${
                    voted ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-black/10 hover:border-[#0052CC]/40"
                  }`}
                >
                  <div className="absolute inset-y-0 left-0 bg-[#0052CC]/10" style={{ width: `${pct}%` }} />
                  <div className="relative flex items-center justify-between px-4 py-3">
                    <span className={`text-sm font-medium ${voted ? "text-[#0A0F1A]" : "text-[#0A0F1A]"}`}>{o.label}</span>
                    <span className="text-sm font-mono text-[#475569]">{pct}% · {o.votes}</span>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="text-xs text-[#475569] mt-4">Clôture : {new Date(p.closes_at).toLocaleDateString("fr-FR", { dateStyle: "long" })}</div>
        </div>
      ))}
      {polls.length === 0 && (
        <div className="py-12 text-center text-sm text-[#475569]">Aucun sondage en cours.</div>
      )}
    </div>
  );
}

function Profil({ user }) {
  return (
    <div data-testid="profil-tab" className="max-w-2xl space-y-5">
      <h2 className="font-display text-2xl text-[#0A0F1A]">Mon profil</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Nom" value={user.name} />
        <Field label="Email" value={user.email} />
        <Field label="Statut" value={user.role === "admin" ? "Administrateur" : "Membre"} />
        <Field label="Méthode d'authentification" value={user.auth_method === "google" ? "Google" : "Email + Mot de passe"} />
        <Field label="Membre depuis" value={new Date(user.created_at).toLocaleDateString("fr-FR")} />
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="rounded-md bg-[#FAFAFA] border border-black/5 px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569]">{label}</div>
      <div className="text-sm font-medium text-[#0A0F1A] mt-1">{value}</div>
    </div>
  );
}
